import React from 'react';
import { useGlobalization } from '@Translations';
import { InputText, StylovyzeDialog, StylovyzeFormV2 } from '@innovyze/stylovyze';
import { FormikHelpers, FormikValues } from 'formik';
import { getApiErrorMessage, isDuplicateNameError } from '@Utils/apiError';
import { selectDeteriorationModelsCards } from '@Selectors';
import { isAlphanumericName, isBlankName, isDuplicateName } from '@Utils/nameValidation';

interface RenameDeteriorationModelDialogProps {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	onRename: (newModelName: string) => Promise<void>;
	modelName: string;
}

export const RenameDeteriorationModelDialog = ({
	isOpen,
	setIsOpen,
	onRename,
	modelName,
}: RenameDeteriorationModelDialogProps) => {
	const { t } = useGlobalization();
	const deteriorationModelsCards = selectDeteriorationModelsCards();

	const handleSubmit = async (values: FormikValues, { setSubmitting, setFieldError }: FormikHelpers<FormikValues>) => {
		setSubmitting(true);
		try {
			await onRename(values.modelName);
		} catch (e) {
			if (e instanceof Error && (e as Error & { isDuplicateName?: boolean }).isDuplicateName) {
				setFieldError('modelName', e.message);
			} else if (isDuplicateNameError(e)) {
				setFieldError('modelName', getApiErrorMessage(e, t('Enter a unique name')));
			}
			setSubmitting(false);
		}
	};

	return (
		<StylovyzeFormV2
			initialValues={{
				modelName: modelName,
			}}
			validate={values => {
				const errors: { [key: string]: string } = {};
				if (isBlankName(values.modelName)) {
					errors.modelName = 'Required';
				} else if (!isAlphanumericName(values.modelName)) {
					errors.modelName = t('Only alphanumeric characters are allowed');
				} else if (
					isDuplicateName(
						values.modelName,
						deteriorationModelsCards.cards.map(card => card.name),
						modelName,
					)
				) {
					errors.modelName = t('Enter a unique name');
				}
				return errors;
			}}
			onSubmit={handleSubmit}>
			{({ values, submitForm, isSubmitting, errors }) => {
				return (
					<StylovyzeDialog
						title={t('Rename Deterioration Model')}
						open={isOpen}
						confirmText={t('OK')}
						confirmButtonProps={{
							disabled: isSubmitting,
						}}
						onConfirm={submitForm}
						cancelText={t('Cancel')}
						onCancel={() => setIsOpen(false)}>
						<InputText
							fieldAttrs={{ name: 'modelName' }}
							textField={{
								label: t('New Name'),
								fullWidth: true,
								error: !!errors.modelName,
								helperText: errors.modelName ?? '',
								value: values.modelName,
							}}
						/>
					</StylovyzeDialog>
				);
			}}
		</StylovyzeFormV2>
	);
};
